import * as Leaflet from 'leaflet';
import {
  POPUP_HOVER_CLOSE_DELAY_MS,
  POPUP_CLICK_RESET_DELAY_MS,
  POPUP_DEBUG_VIEW,
  POPUP_ALWAYS_OPEN,
} from './mapview-constants';
import { safeCall } from './utils/safe-call';
import { buildHoverContentHtml, escapeHtml } from './utils/mapview-hover-format';

/**
 * Build popup HTML content for markers and geojson features.
 *
 * Handles three types: 'monitor' (monitoring station), 'observation', and 'feature' (geojson feature).
 *
 * For 'monitor', uses station name or identifier.
 * For 'observation', uses location and date.
 * For 'feature', uses properties and layer metadata.
 *
 * @param type Type of popup: 'monitor', 'observation', or 'feature'
 * @param data Data object for the popup (station, observation, or geojson feature)
 * @param layerData Optional layer metadata for 'feature' type
 * @returns HTML string for popup content
 */
export function buildPopupContent(
  type: 'monitor' | 'observation' | 'feature',
  data: any,
  layerData?: any,
  pane?: string
): string {
  try {
    if (type === 'monitor') {
      const display =
        data?.human_readable_name || data?.source_identifier || data?.name || data?.display_name || data?.siteName;
      let popup = `<b>${display}</b>`;

      const detail = data?.source_metadata?.pin_detail;
      const url = data?.source_metadata?.pin_url;
      const urlLabel = data?.source_metadata?.pin_url_label;

      if (detail) popup += `<div class="pin-section">${detail}</div>`;
      if (url) popup += `<div class="pin-section"><a href="${url}" target="_blank">${urlLabel || url}</a></div>`;

      return popup;
    }

    if (type === 'observation') {
      const location = data?.location || 'Observation';
      if (data?.date) {
        return `<b>${location}</b><br>${data.date}`;
      }
      return `<b>${location}</b>`;
    }

    const nameAttribute = layerData?.name_attribute || 'name';
    const displayName =
      (nameAttribute && data.properties?.[nameAttribute]) ||
      data.properties?.name ||
      layerData?.layer_name ||
      layerData?.name ||
      'Unnamed Feature';
    let popup = `<b class="hover-title">${escapeHtml(String(displayName))}</b>`;

    // Hover content configured in the layer editor (map API returns snake_case).
    const hoverFormat = layerData?.hover_format ?? layerData?.hoverFormat ?? null;
    const hoverContent = buildHoverContentHtml(hoverFormat, data?.properties);
    popup += hoverContent.html;

    if (POPUP_DEBUG_VIEW && pane) {
      popup += `<div style="color: #000; font-size: 8px; padding: 2px; z-index: 10000;">${pane}</div>`;
    }

    return popup;
  } catch (e) {
    console.warn('buildPopupContent failed', e);
    return '<b>Info</b>';
  }
}

/**
 * Create an observable function to fetch and append observation layout HTML.
 *
 * Uses the provided getObservationLayoutFn to fetch layout HTML by observation ID.
 * Appends location and formatted date to the layout, stripping HTML tags for searchability.
 *
 * @param getObservationLayoutFn Function to fetch layout HTML given an observation ID
 * @param layer Leaflet layer or marker
 * @param opts Options for styles, click handler, z-index, etc.
 * @returns A function that sets the style
 */
function createSafeSetStyle(layer: any) {
  return (style: any) => {
    if (layer && typeof layer.setStyle === 'function' && style !== undefined) {
      safeCall(() => layer.setStyle(style), 'createSafeSetStyle.setStyle', 'debug');
    }
  };
}

/**
 * Create a safe function to set the z-index of a Leaflet layer or marker.
 * @param layer Leaflet layer or marker
 * @returns A function that sets the z-index
 */
function createSafeSetZIndex(layer: any) {
  return (z?: number) => {
    if (layer && typeof layer.setZIndexOffset === 'function' && typeof z === 'number') {
      safeCall(() => layer.setZIndexOffset(z), 'createSafeSetZIndex.setZIndexOffset', 'debug');
    }
  };
}

/**
 * Create a safe function to set the style of a Leaflet layer or marker.
 * @param layer Leaflet layer or marker
 * @param opts Options for styles, click handler, z-index, etc.
 * @param getPopupHovered Function to check if popup is hovered
 * @param safeSetStyle Function to safely set style
 * @param safeSetZIndex Function to safely set z-index
 * @returns A function that closes the popup if not hovered
 */
function createCloseIfNotHovered(
  layer: any,
  opts: any,
  getPopupHovered: () => boolean,
  safeSetStyle: (s: any) => void,
  safeSetZIndex: (z?: number) => void
) {
  return () => {
    if (POPUP_ALWAYS_OPEN) return;
    if (!getPopupHovered()) {
      if (opts.getOriginalStyle) safeSetStyle(opts.getOriginalStyle());
      safeSetZIndex(opts.zIndexDefault);
      if (layer && typeof layer.closePopup === 'function') {
        safeCall(() => layer.closePopup(), 'createCloseIfNotHovered.closePopup', 'debug');
      }
    }
  };
}

/**
 * Create a function to handle mouse enter events on the popup.
 * @param setPopupHovered Function to set popup hovered state
 * @returns A function that sets the popup hovered state to true
 */
function createOnPopupEnter(setPopupHovered: (v: boolean) => void) {
  return () => {
    setPopupHovered(true);
  };
}

/**
 * Create a function to handle mouse leave events on the popup.
 * @param setPopupHovered Function to set popup hovered state
 * @param setHoverTimeout Function to set hover timeout
 * @param closeIfNotHovered Function to close popup if not hovered
 * @returns A function that handles mouse leave events
 */
function createOnPopupLeave(
  setPopupHovered: (v: boolean) => void,
  setHoverTimeout: (t: ReturnType<typeof setTimeout> | undefined) => void,
  closeIfNotHovered: () => void
) {
  return () => {
    setPopupHovered(false);
    setHoverTimeout(setTimeout(() => closeIfNotHovered(), POPUP_HOVER_CLOSE_DELAY_MS));
  };
}

/**
 * Create a function to handle mouse over events on the layer.
 *
 * Adds DOM event listeners to the popup when opened.
 *
 * @param layer Leaflet layer or marker
 * @param opts Options for styles, click handler, z-index, etc.
 * @param safeSetStyle Safe function to set style
 * @param safeSetZIndex Safe function to set z-index
 * @param onPopupEnter Function to handle mouse enter events
 * @param onPopupLeave Function to handle mouse leave events
 * @param getHoverTimeout Function to get hover timeout
 * @param setHoverTimeout Function to set hover timeout
 * @returns A function that handles mouse over events
 */
function createMouseover(
  layer: any,
  opts: any,
  safeSetStyle: (s: any) => void,
  safeSetZIndex: (z?: number) => void,
  onPopupEnter: () => void,
  onPopupLeave: () => void,
  getHoverTimeout: () => ReturnType<typeof setTimeout> | undefined,
  setHoverTimeout: (t: ReturnType<typeof setTimeout> | undefined) => void
) {
  return () => {
    const ht = getHoverTimeout();
    if (ht) clearTimeout(ht);
    if (opts.bringToFront && layer && typeof layer.bringToFront === 'function') {
      safeCall(() => layer.bringToFront(), 'createMouseover.bringToFront', 'debug');
    }
    if (opts.getHighlightStyle) safeSetStyle(opts.getHighlightStyle());
    safeSetZIndex(opts.zIndexOnHover);

    if (layer && typeof layer.openPopup === 'function') {
      safeCall(() => layer.openPopup(), 'createMouseover.openPopup', 'debug');
    }

    try {
      const popup = layer.getPopup && layer.getPopup();
      const popupElement = popup && popup.getElement && popup.getElement();
      if (popupElement) {
        safeCall(
          () => popupElement.addEventListener('mouseenter', onPopupEnter),
          'createMouseover.addEventListener.mouseenter',
          'debug'
        );
        safeCall(
          () => popupElement.addEventListener('mouseleave', onPopupLeave),
          'createMouseover.addEventListener.mouseleave',
          'debug'
        );
      }
    } catch (e) {
      console.debug('Attaching popup element listeners failed', e);
    }
  };
}

/**
 * Create a function to handle mouse leave events on the popup.
 * @param setHoverTimeout Function to set hover timeout
 * @param closeIfNotHovered Function to close popup if not hovered
 * @returns A function that handles mouse leave events
 */
function createMouseout(
  setHoverTimeout: (t: ReturnType<typeof setTimeout> | undefined) => void,
  closeIfNotHovered: () => void
) {
  return () => {
    setHoverTimeout(setTimeout(() => closeIfNotHovered(), 300));
  };
}

/**
 * Create a function to handle mouse over events on the popup.
 * @param getHoverTimeout Function to get hover timeout
 * @param setHoverTimeout Function to set hover timeout
 * @param opts Options for styles, click handler, z-index, etc.
 * @param safeSetStyle Safe function to set style
 * @param safeSetZIndex Safe function to set z-index
 * @param getPopupHovered Function to get popup hovered state
 * @returns A function that handles click events
 */
function createClick(
  getHoverTimeout: () => ReturnType<typeof setTimeout> | undefined,
  setHoverTimeout: (t: ReturnType<typeof setTimeout> | undefined) => void,
  opts: any,
  safeSetStyle: (s: any) => void,
  safeSetZIndex: (z?: number) => void,
  getPopupHovered: () => boolean
) {
  return (ev?: any) => {
    const ht = getHoverTimeout();
    if (ht) clearTimeout(ht);
    setHoverTimeout(undefined);
    if (opts.getHighlightStyle) safeSetStyle(opts.getHighlightStyle());
    safeSetZIndex(opts.zIndexOnHover);
    if (opts.onClick) safeCall(() => opts.onClick(ev), 'createClick.onClick', 'warn');
    setTimeout(() => {
      if (!getPopupHovered()) {
        if (opts.getOriginalStyle) safeSetStyle(opts.getOriginalStyle());
        safeSetZIndex(opts.zIndexDefault);
      }
    }, POPUP_CLICK_RESET_DELAY_MS);
  };
}

/**
 * Attach consistent popup/hover/click behaviour to a Leaflet layer/marker.
 * Returns a disposer to remove attached handlers and popup element listeners.
 *
 * Attaches mouseover, mouseout, and click handlers to the layer.
 * Also attaches mouseenter and mouseleave handlers to the popup element when opened.
 * Leaflet layers may not support all methods, so safeCall is used to avoid errors.
 *
 * @param layer Leaflet layer or marker
 * @param opts Options for styles, click handler, z-index, etc.
 * @returns Disposer function to remove handlers
 */
export function attachPopupHoverHandlers(
  layer: any,
  opts: {
    getOriginalStyle?: () => any;
    getHighlightStyle?: () => any;
    onClick?: (ev?: any) => void;
    zIndexOnHover?: number;
    zIndexDefault?: number;
    bringToFront?: boolean;
  }
): () => void {
  let hoverTimeout: ReturnType<typeof setTimeout> | undefined;
  let popupHovered = false;

  const getHoverTimeout = () => hoverTimeout;
  const setHoverTimeout = (t: ReturnType<typeof setTimeout> | undefined) => {
    hoverTimeout = t;
  };
  const getPopupHovered = () => popupHovered;
  const setPopupHovered = (v: boolean) => {
    popupHovered = v;
  };

  const safeSetStyle = createSafeSetStyle(layer);
  const safeSetZIndex = createSafeSetZIndex(layer);
  const closeIfNotHovered = createCloseIfNotHovered(layer, opts, getPopupHovered, safeSetStyle, safeSetZIndex);
  const onPopupEnter = createOnPopupEnter(setPopupHovered);
  const onPopupLeave = createOnPopupLeave(setPopupHovered, setHoverTimeout, closeIfNotHovered);
  const mouseover = createMouseover(
    layer,
    opts,
    safeSetStyle,
    safeSetZIndex,
    onPopupEnter,
    onPopupLeave,
    getHoverTimeout,
    setHoverTimeout
  );
  const mouseout = createMouseout(setHoverTimeout, closeIfNotHovered);
  const click = createClick(getHoverTimeout, setHoverTimeout, opts, safeSetStyle, safeSetZIndex, getPopupHovered);

  if (layer && typeof layer.on === 'function') {
    safeCall(() => layer.on('mouseover', mouseover), 'attachPopupHoverHandlers.on.mouseover', 'debug');
    safeCall(() => layer.on('mouseout', mouseout), 'attachPopupHoverHandlers.on.mouseout', 'debug');
    safeCall(() => layer.on('click', click), 'attachPopupHoverHandlers.on.click', 'debug');
  } else {
    console.debug('attachPopupHoverHandlers: layer does not support .on');
  }

  return () => {
    if (layer && typeof layer.off === 'function') {
      safeCall(() => layer.off('mouseover', mouseover), 'attachPopupHoverHandlers.off.mouseover', 'debug');
      safeCall(() => layer.off('mouseout', mouseout), 'attachPopupHoverHandlers.off.mouseout', 'debug');
      safeCall(() => layer.off('click', click), 'attachPopupHoverHandlers.off.click', 'debug');
    }
    try {
      const popup = layer.getPopup && layer.getPopup();
      const popupElement = popup && popup.getElement && popup.getElement();
      if (popupElement) {
        safeCall(
          () => popupElement.removeEventListener('mouseenter', onPopupEnter),
          'attachPopupHoverHandlers.removeEventListener.mouseenter',
          'debug'
        );
        safeCall(
          () => popupElement.removeEventListener('mouseleave', onPopupLeave),
          'attachPopupHoverHandlers.removeEventListener.mouseleave',
          'debug'
        );
      }
    } catch (e) {
      console.debug('Failed to remove popup element listeners', e);
    }
  };
}
